import { useState, type FormEvent } from 'react';
import { Anchor, Box, Button, Card, Center, Image, PasswordInput, Stack, Text } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset } from '@/api/auth';
import { ApiError } from '@/api/client';
import { usePageTitle } from '@/components/usePageTitle';
import { forest } from '@/theme/theme';

export const Route = createFileRoute('/reset-password_/$uid/$token')({ component: ResetPasswordConfirmPage });

function ResetPasswordConfirmPage() {
  const { t } = useTranslation();
  usePageTitle(t('auth.setPassword'));
  const { uid, token } = Route.useParams();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(uid, token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_token') {
        setError(t('auth.invalidToken'));
      } else if (err instanceof ApiError && err.status === 400) {
        // The backend's only other 400 on this endpoint is a Django password-validator
        // failure, shaped as DRF field errors ({"password": [...]}) rather than the
        // {error:{code,message}} envelope `unwrap()` understands, so the specific
        // validator message isn't available here — show the generic weak-password copy.
        setError(t('auth.weakPassword'));
      } else {
        setError(t('auth.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box pos="relative" h="100vh" bg={forest[9]} style={{ overflow: 'hidden' }}>
      <Box
        pos="absolute"
        inset={0}
        style={{
          backgroundImage: 'url(/rastr.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.28,
        }}
      />
      <Center pos="relative" h="100%">
        <Stack align="center" gap="xl">
          <Image src="/logo-white.png" alt="Forestum" w={160} fit="contain" />
          <Card w={360}>
            {done ? (
              <Stack gap="md">
                <Text>{t('auth.resetDone')}</Text>
                <Anchor href="/login" size="sm" ta="center">
                  {t('auth.signIn')}
                </Anchor>
              </Stack>
            ) : (
              <form onSubmit={(e) => void handleSubmit(e)}>
                <Stack gap="md">
                  <PasswordInput
                    label={t('auth.newPassword')}
                    autoComplete="new-password"
                    required
                    withAsterisk={false}
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                  />
                  {error && (
                    <Text c="red" role="alert">
                      {error}
                    </Text>
                  )}
                  <Button type="submit" fullWidth loading={submitting}>
                    {t('auth.setPassword')}
                  </Button>
                </Stack>
              </form>
            )}
          </Card>
        </Stack>
      </Center>
    </Box>
  );
}
